import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  CircleDot,
  Clock,
  FileBarChart,
  FilePenLine,
  FileStack,
  FileText,
  LayoutList,
  ListOrdered,
  PenLine,
  Scale,
  ScanLine,
  ScrollText,
  Search,
  Shield,
  Upload,
  Users,
} from "lucide-react";

function matches(message: string, pattern: string): boolean {
  return message.toLowerCase().includes(pattern.toLowerCase());
}

function isDraftProgress(step: string | undefined, message: string): boolean {
  const normalizedStep = step?.toLowerCase() ?? "";
  const normalizedMessage = message.toLowerCase();
  return (
    normalizedStep === "resolve-template" ||
    normalizedStep === "plan-structure" ||
    normalizedStep === "draft-sections" ||
    normalizedStep === "assemble-draft" ||
    matches(normalizedMessage, "drafting workflow") ||
    matches(normalizedMessage, "draft document") ||
    matches(normalizedMessage, "contract sections drafted") ||
    matches(normalizedMessage, "template structure resolved") ||
    matches(normalizedMessage, "document structure planned")
  );
}

export function isProgressLogError(step: string | undefined, message: string): boolean {
  const normalizedStep = step?.toLowerCase() ?? "";
  const normalizedMessage = message.toLowerCase();
  return (
    normalizedStep === "error" ||
    normalizedMessage.includes("error") ||
    normalizedMessage.includes("failed")
  );
}

export function isProgressLogComplete(message: string): boolean {
  const normalizedMessage = message.toLowerCase();
  return (
    normalizedMessage.includes(" resolved") ||
    normalizedMessage.includes("structure planned") ||
    normalizedMessage.includes("sections drafted") ||
    normalizedMessage.includes("document ready") ||
    normalizedMessage.includes("generated successfully")
  );
}

export function getProgressLogIcon(step: string | undefined, message: string): LucideIcon {
  const normalizedStep = step?.toLowerCase() ?? "";
  const normalizedMessage = message.toLowerCase();

  if (isProgressLogError(step, message)) {
    return AlertCircle;
  }

  if (isProgressLogComplete(message)) {
    return CheckCircle2;
  }

  if (normalizedStep === "upload" || matches(normalizedMessage, "receiving and saving")) {
    return Upload;
  }

  if (normalizedStep === "parse" || matches(normalizedMessage, "extracting text")) {
    return FileText;
  }

  if (
    normalizedStep === "parsed" ||
    (matches(normalizedMessage, "parsed") && matches(normalizedMessage, "sections"))
  ) {
    return LayoutList;
  }

  if (isDraftProgress(step, message)) {
    if (
      normalizedStep === "workflow-start" ||
      matches(normalizedMessage, "starting drafting workflow")
    ) {
      return PenLine;
    }

    if (
      normalizedStep === "resolve-template" ||
      matches(normalizedMessage, "resolving template") ||
      matches(normalizedMessage, "template and role context")
    ) {
      return ScrollText;
    }

    if (normalizedStep === "plan-structure" || matches(normalizedMessage, "planning document structure")) {
      return ListOrdered;
    }

    if (normalizedStep === "draft-sections" || matches(normalizedMessage, "drafting contract sections")) {
      return FilePenLine;
    }

    if (normalizedStep === "assemble-draft" || matches(normalizedMessage, "assembling")) {
      return FileStack;
    }

    if (matches(normalizedMessage, "preparing workflow data")) {
      return Users;
    }

    return FilePenLine;
  }

  if (
    normalizedStep === "workflow-start" ||
    matches(normalizedMessage, "starting contract review workflow")
  ) {
    return Scale;
  }

  if (normalizedStep === "parse-document" || matches(normalizedMessage, "parsing document")) {
    return ScanLine;
  }

  if (
    normalizedStep.startsWith("mapping") ||
    matches(normalizedMessage, "preparing workflow data") ||
    matches(normalizedMessage, "processing document structure")
  ) {
    return LayoutList;
  }

  if (normalizedStep === "analyze-sections" || matches(normalizedMessage, "analyzing contract sections")) {
    return Search;
  }

  if (normalizedStep === "assess-risks" || matches(normalizedMessage, "assessing risk")) {
    return Shield;
  }

  if (normalizedStep === "research-precedents" || matches(normalizedMessage, "researching legal precedents")) {
    return BookOpen;
  }

  if (
    normalizedStep === "generate-report" ||
    matches(normalizedMessage, "generating review report")
  ) {
    return FileBarChart;
  }

  if (matches(normalizedMessage, "still working")) {
    return Clock;
  }

  if (matches(normalizedMessage, "processing supporting document")) {
    return FileText;
  }

  return CircleDot;
}
